import { apiClient } from './api-client';
import { sendWithEncryptedPasswords } from '@/lib/credentialEncryption';

// User types
export type UserRole = 'admin' | 'supervisor' | 'agent';
export type UserStatus = 'active' | 'inactive' | 'suspended';

export interface User {
  id: string;
  tenantId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  avatarUrl?: string;
  role: UserRole;
  status: UserStatus;
  isVerified: boolean;
  mfaEnabled: boolean;
  lastLoginAt?: string;
  profile?: Record<string, any>;
  preferences?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface Role {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  permissions: string[];
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserFilter {
  search?: string;
  role?: UserRole;
  status?: UserStatus;
  page?: number;
  limit?: number;
}

export interface CreateUserDto {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  role?: UserRole;
}

export interface UpdateUserDto {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  avatarUrl?: string;
  role?: UserRole;
  status?: UserStatus;
}

export interface UpdateProfileDto {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  avatarUrl?: string;
  timezone?: string;
  locale?: string;
  /** App nav rail collapsed to glyphs (wide screens). Stored in `preferences`. */
  sidebarCollapsed?: boolean;
}

export interface CreateRoleDto {
  name: string;
  description?: string;
  permissions: string[];
}

export interface UpdateRoleDto {
  name?: string;
  description?: string;
  permissions?: string[];
}

export interface NotificationPreferences {
  emailEnabled: boolean;
  inAppEnabled: boolean;
  pushEnabled: boolean;
  notifyOnAssignment: boolean;
  notifyOnReply: boolean;
  notifyOnMention: boolean;
  notifyOnSlaBreach: boolean;
  notifyOnEscalation: boolean;
  notifyOnSlaEscalation: boolean;
  dailyDigest: boolean;
  weeklySummary: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const settingsService = {
  // ============== USER MANAGEMENT ==============

  async getUsers(filter?: UserFilter): Promise<PaginatedResponse<User>> {
    const params = new URLSearchParams();
    if (filter) {
      if (filter.search) params.append('search', filter.search);
      if (filter.role) params.append('role', filter.role);
      if (filter.status) params.append('status', filter.status);
      if (filter.page) params.append('page', String(filter.page));
      if (filter.limit) params.append('limit', String(filter.limit));
    }
    const response = await apiClient.get(`/settings/users?${params.toString()}`);
    return response.data;
  },

  async getUser(id: string): Promise<User> {
    const response = await apiClient.get(`/settings/users/${id}`);
    return response.data;
  },

  async createUser(dto: CreateUserDto): Promise<User> {
    const response = await sendWithEncryptedPasswords(dto, (body) => apiClient.post('/settings/users', body));
    return response.data;
  },

  async updateUser(id: string, dto: UpdateUserDto): Promise<User> {
    const response = await apiClient.put(`/settings/users/${id}`, dto);
    return response.data;
  },

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/settings/users/${id}`);
  },

  async activateUser(id: string): Promise<User> {
    const response = await apiClient.post(`/settings/users/${id}/activate`);
    return response.data;
  },

  async deactivateUser(id: string): Promise<User> {
    const response = await apiClient.post(`/settings/users/${id}/deactivate`);
    return response.data;
  },

  // ============== CURRENT USER PROFILE ==============

  async getProfile(): Promise<User> {
    const response = await apiClient.get('/settings/profile');
    return response.data;
  },

  async updateProfile(dto: UpdateProfileDto): Promise<User> {
    const response = await apiClient.put('/settings/profile', dto);
    return response.data;
  },

  // ============== ROLE MANAGEMENT ==============

  async getRoles(): Promise<Role[]> {
    const response = await apiClient.get('/settings/roles');
    return response.data;
  },

  async getRole(id: string): Promise<Role> {
    const response = await apiClient.get(`/settings/roles/${id}`);
    return response.data;
  },

  async getAvailablePermissions(): Promise<string[]> {
    const response = await apiClient.get('/settings/roles/permissions');
    return response.data;
  },

  async createRole(dto: CreateRoleDto): Promise<Role> {
    const response = await apiClient.post('/settings/roles', dto);
    return response.data;
  },

  async updateRole(id: string, dto: UpdateRoleDto): Promise<Role> {
    const response = await apiClient.put(`/settings/roles/${id}`, dto);
    return response.data;
  },

  async deleteRole(id: string): Promise<void> {
    await apiClient.delete(`/settings/roles/${id}`);
  },

  async assignRoleToUser(roleId: string, userId: string): Promise<void> {
    await apiClient.post(`/settings/roles/${roleId}/assign/${userId}`);
  },

  async removeRoleFromUser(roleId: string, userId: string): Promise<void> {
    await apiClient.delete(`/settings/roles/${roleId}/remove/${userId}`);
  },

  // ============== NOTIFICATION PREFERENCES ==============

  async getNotificationPreferences(): Promise<NotificationPreferences> {
    const response = await apiClient.get('/settings/profile/notifications');
    return response.data;
  },

  async updateNotificationPreferences(
    prefs: Partial<NotificationPreferences>
  ): Promise<NotificationPreferences> {
    const response = await apiClient.put('/settings/profile/notifications', prefs);
    return response.data;
  },

};

export default settingsService;
